import apiClient from '@/lib/api-client';
import type { Facet, Suggesties } from '@xxtract/tabelfilters';

export interface ClientSummary {
  id: string;
  name: string;
  glnNumber: string;
  overallScore: number;
  productCount: number;
  categoryScores: CategoryScore[];
  lastUpdated: string;
}

export interface CategoryScore {
  categoryId: string;
  categoryName: string;
  score: number;
  fieldCount: number;
  filledCount: number;
}

export interface CategoryStatus {
  categoryId: string;
  score: number;
  applicable: boolean;
}

export interface Product {
  id: string;
  gtin: string;
  name: string;
  brand: string;
  /** Ondoorzichtig merk-id (`brand-…`); `null` zonder merk. Waarde van het merkfilter (story 9). */
  brandId?: string | null;
  category: string;
  score: number;
  photoUrl: string;
  lastUpdated: string;
  categoryScores?: CategoryStatus[];
  /** Count of still-empty required fields across all categories (h2-2 list fidelity fix). */
  missingRequiredFieldCount?: number;
}

export interface ProductDetailGtin {
  gtin: string;
  description: string;
  packagingLayer: string;
  overallScore: number;
  categoryScores: Record<string, { score: number; categoryName: string; applicable: boolean }>;
  missingFields: Array<{ fieldName: string; category: string }>;
}

export interface ProductDetailCategory {
  categoryId: string;
  categoryName: string;
  score: number;
  applicable: boolean;
  fields: Array<{
    fieldName: string;
    status: 'complete' | 'missing';
    packagingLayer?: string;
  }>;
}

export interface ProductDetail {
  cinXmlId: string;
  productName: string;
  overallScore: number;
  gtins: ProductDetailGtin[];
  categories: ProductDetailCategory[];
  lastUpdated: string;
}

export interface ProductField {
  fieldId: string;
  fieldName: string;
  category: string;
  isRequired: boolean;
  isComplete: boolean;
  value: string | null;
}

/** spec-tabelfilters story 9: lijststand + paginafilters als queryparameters (`productLijst.ts`). */
export interface ProductsQuery {
  clientId?: string;
  parameters: Record<string, string>;
}

export interface ProductsResponse {
  data: Product[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    facetten?: Record<string, Facet[]>;
    suggesties?: Suggesties;
    afgekapt?: boolean;
  };
}

interface BackendClientSummary {
  gln: string;
  companyName: string;
  totalProducts: number;
  overallScore: number;
  categoryScores: Record<string, number>;
  lastUpdated: string;
}

function mapClientSummary(backend: BackendClientSummary): ClientSummary {
  return {
    id: backend.gln,
    name: backend.companyName,
    glnNumber: backend.gln,
    overallScore: backend.overallScore,
    productCount: backend.totalProducts,
    // The client list endpoint returns aggregate scores without field-level counts.
    // fieldCount and filledCount are derived from the dashboard endpoint (per-client detail).
    categoryScores: Object.entries(backend.categoryScores).map(
      ([name, score]) => ({
        categoryId: name,
        categoryName: name,
        score,
        fieldCount: backend.totalProducts,
        filledCount: Math.round(backend.totalProducts * (score / 100)),
      }),
    ),
    lastUpdated: backend.lastUpdated,
  };
}

export async function getClientSummaries(): Promise<{ data: ClientSummary[] }> {
  const response = await apiClient.get<{ data: BackendClientSummary[] }>(
    '/completeness/clients',
  );
  return {
    data: response.data.data.map(mapClientSummary),
  };
}

export async function getClientDetail(
  clientId: string,
): Promise<{ data: ClientSummary }> {
  const response = await apiClient.get<{ data: ClientSummary }>(
    `/completeness/clients/${clientId}`,
  );
  return response.data;
}

export async function getProducts(
  query: ProductsQuery,
): Promise<ProductsResponse> {
  const params = new URLSearchParams(query.parameters);
  const clientId = query.clientId || '';
  const response = await apiClient.get<ProductsResponse>(
    `/completeness/clients/${clientId}/products?${params.toString()}`,
  );
  return response.data;
}

export async function getProductDetail(
  clientId: string,
  productId: string,
): Promise<{ data: ProductDetail }> {
  const response = await apiClient.get<{ data: ProductDetail }>(
    `/completeness/clients/${clientId}/products/${productId}`,
  );
  return response.data;
}

export interface DashboardData {
  overallScore: number;
  totalProducts: number;
  categories: Array<
    CategoryScore & {
      incompleteCount: number;
    }
  >;
  lastUpdated: string;
}

export interface BrandOption {
  id: string;
  name: string;
  boundaryWhitespace?: {
    leading: Array<{ codePoint: string; count: number }>;
    trailing: Array<{ codePoint: string; count: number }>;
  };
  productCount: number;
  score: number;
  brandOwnerGln: string;
  logoUrl?: string;
  categoryScores?: CategoryStatus[];
}

export interface AttentionProduct {
  id: string;
  gtin: string;
  name: string;
  brand: string;
  brandId: string | null;
  score: number;
  missingRequiredFieldCount: number;
  lastUpdated: string;
}

export async function getClientDashboard(
  clientId: string,
  params?: { brandId?: string; categoryId?: string },
): Promise<{ data: DashboardData }> {
  const searchParams = new URLSearchParams();
  if (params?.brandId) searchParams.set('brandId', params.brandId);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  const qs = searchParams.toString();
  const response = await apiClient.get<{ data: DashboardData }>(
    `/completeness/clients/${clientId}/dashboard${qs ? `?${qs}` : ''}`,
  );
  return response.data;
}

export async function getClientBrands(
  clientId: string,
): Promise<{ data: BrandOption[] }> {
  const response = await apiClient.get<{ data: BrandOption[] }>(
    `/completeness/clients/${clientId}/brands`,
  );
  return response.data;
}

export async function getAttentionProducts(
  gln: string,
): Promise<{ data: AttentionProduct[] }> {
  const params = new URLSearchParams({ gln, limit: '5' });
  const response = await apiClient.get<{ data: AttentionProduct[] }>(
    `/completeness/attention?${params.toString()}`,
  );
  return response.data;
}

export type ClientTrend = 'improving' | 'stable' | 'declining';

export interface ClientEnrichment {
  gln: string;
  /** Null when procesManagement is unavailable — the rest of the row (score, trend) stays intact (NFR4). */
  openOrderCount: number | null;
  /** Null when this client's trend read failed. */
  trend: ClientTrend | null;
}

/** h4-4/FR28: klantoverzicht-verrijking (openstaande orders + completeness-trend). */
export async function getClientsEnrichment(): Promise<{ data: ClientEnrichment[]; meta: { ordersDegraded: boolean } }> {
  const response = await apiClient.get<{ data: ClientEnrichment[]; meta: { ordersDegraded: boolean } }>(
    '/clients/enrichment',
  );
  return response.data;
}
